import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";
import { useApp } from "../App/useApp";
import { useVuelidate } from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";

import config from "../../common/config";
import { ToastrService } from "../../services/toastr.service";
import { AgencyService } from "../../services/agency.service";
import {
    ApplicationEnum,
    ApplicationNames,
    LineOfServices,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../enums/enums";
import { ProvidersService } from "../../services/providers-service";
import { BranchesService } from "../../services/branches-service";
import { IGetPatientsByNameQuery, PatientService } from "../../services/patient.service";
import { PermissionManager } from "../../common/utilities/permission-manager";
import { CentralIntakeService } from "../../services/central-intake.service";
import { ReferralService } from "../../services/referral-service";
import { LineOfService } from "./LineOfService.model";
import { ICreateInterimReferralPayload } from "../../resources-vue/vue-interfaces/i-get-referral";
import { formatPhone, formatDateFromDate } from "../../common/vue-helpers/modifiers/value-modifier";
import { formatName } from "../../common/format-name";
import { IGetPatientsByNameResponse } from "../../resources-vue/vue-interfaces/i-patient-service";
import { PAGE_FAIL_RESPONSE } from "../CentralIntake/Inquiry/CentralInquiry";
import { IGetProviderItem } from "../../resources-vue/vue-interfaces/i-providers-service";
import { IBranchResponse } from "../../resources-vue/vue-interfaces/i-branch";
import { ICheckPermissions } from "../../resources-vue/vue-interfaces/i-permissions";
import { AnalyticsCategory, AnalyticsService } from "../../services/analytics.service";

import PageLayout from "../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import FormSection from "../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import Pagination from "../../resources-vue/vue-elements/Pagination/Pagination.vue";
import ErrorMessageHandler from "../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import FormatName from "../../resources-vue/vue-custom-elements/FormatName/FormatName.vue";
import EmptyTextHandler from "../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import CreateInterimReferralRow from "./CreateInterimReferralRow.vue";
import BranchDefaultFilter from "../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import SortableTh from "../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";

export default defineComponent({
    components: {
        PageLayout,
        FormSection,
        Pagination,
        ErrorMessageHandler,
        FormatName,
        EmptyTextHandler,
        CreateInterimReferralRow,
        BranchDefaultFilter,
        SortableTh
    },

    data() {
        return {
            _analyticsService: null as AnalyticsService,
            name: "Create",
            _providersService: null,
            _referralService: null,
            _branchesService: null,
            _permissionManager: null,
            _centralIntakeService: null,
            _patientsService: null,
            _agencyService: null,
            _toastrService: null,
            searchPatientTerm: "",
            selectedBranch: null,
            inquiry: null,
            sortOrder: "asc",
            sortBy: "dateOfBirth",
            selectedPrimaryLineOfService: null,
            lineOfService: this.initNewPatientReferral(ApplicationEnum.AxxessHospiceFE),
            isLineOfServiceFormRequested: false,
            providerList: [],
            allProviderList: [],
            isAddingReferralLoading: false,
            accountId: "",
            primaryLineOfServiceList: [],
            showNewReferral: false as boolean,
            showNewReferralHospice: false,
            showNewReferralPalliative: false,
            allBranchList: [],
            selectedBranches: [] as string[],
            showNewReferralHomeHealth: false,
            showNewReferralHomeCare: false,
            hospiceApplicationId: ApplicationEnum.AxxessHospiceFE,
            palliativeApplicationId: ApplicationEnum.AxxessPalliative,
            homeHealthApplicationId: ApplicationEnum.AgencyCore,
            homeCareApplicationId: ApplicationEnum.HomeCare,
            lineOfServiceEnumMap: LineOfServices,
            isNonMedical: false,
            currentApplication: null,
            agencyApplicationList: [],
            hasInquiryId: false as boolean,
            showPatientList: false,
            columns: 8,
            isSearchLoading: false,
            isSearchError: false,
            patientListPagination: null,
            isError: false,
            page: null,
            inquiryApplication: null as number,
            ApplicationNamesEnum: ApplicationNames,
            pageSize: 10,
            isReferredToAnotherSolution: false as boolean,
            isNonAdmit: false as boolean,
            checkProductPermission: {} as ICheckPermissions,
            hasDocViewPermission: {},
            formatDateFromDate,
            formatPhone,
            firstName: "" as string,
            lastName: "" as string,
            v$: useVuelidate(),
            agencyService: [
                {
                    name: "Hospice",
                    show: this.showNewReferralHospice,
                    id: ApplicationEnum.AxxessHospiceFE,
                    isNonMedical: false
                },
                {
                    name: "Palliative",
                    show: this.showNewReferralPalliative,
                    id: ApplicationEnum.AxxessPalliative,
                    isNonMedical: false
                },
                {
                    name: "Home Health",
                    show: this.showNewReferralHomeHealth,
                    id: ApplicationEnum.AgencyCore,
                    isNonMedical: false
                },
                {
                    name: "Home Care (Medical)",
                    show: this.showNewReferralHomeCare,
                    id: ApplicationEnum.HomeCare,
                    isNonMedical: false
                },
                {
                    name: "Home Care (Non-Medical)",
                    show: this.showNewReferralHomeCare,
                    id: ApplicationEnum.HomeCare,
                    isNonMedical: true
                }
            ],
            isConvertBackToPending: false as boolean
        };
    },

    validations() {
        return {
            selectedBranch: {
                id: {
                    required
                }
            },
            lineOfService: {
                firstName: { required },
                lastName: { required }
            },
            selectedPrimaryLineOfService: {
                required: requiredIf(() => this.currentApplication === ApplicationEnum.AxxessHospiceFE)
            }
        };
    },

    async created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._providersService = Container.instance.get(ProvidersService);
        this._branchesService = Container.instance.get(BranchesService);
        this._patientsService = Container.instance.get(PatientService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._referralService = Container.instance.get(ReferralService);
        this._agencyService = Container.instance.get(AgencyService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.patientListPagination = this.$refs.patientListPagination;
        const providers = await this._providersService.getProvider();
        this.allBranchList = await this._branchesService.getAllBranches();
        this.allProviderList = providers.filter((provider: any) => provider.branches.length > 0);
    },

    async mounted() {
        const organizationId = localStorage.getItem(config.organizationId);
        const selectOrganization = this.organizations.filter((organization) => organization.id == organizationId);

        if (this.$route.query.firstName) {
            this.firstName = this.$route.query.firstName as string;
            this.lastName = this.$route.query.lastName as string;
            this.searchPatientTerm = `${this.lastName}, ${this.firstName}`;
        }

        if (this.$route.query.inquiryId) {
            this.hasInquiryId = true;
            this.inquiryApplication = Number(this.$route.query.application);
        }

        const selectedAgencies = selectOrganization[0].agencies;
        const agencyList = selectedAgencies.map((agency) => {
            let agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            this.agencyApplicationList.push(application);
            return {
                ...agency,
                application: application
            };
        });

        const showNewReferralHos = agencyList.some((el: any) => el.application === ApplicationEnum.AxxessHospiceFE);
        const showNewReferralPal = agencyList.some((el: any) => el.application === ApplicationEnum.AxxessPalliative);
        const showNewReferralHc = agencyList.some((el: any) => el.application === ApplicationEnum.HomeCare);
        const showNewReferralHh = agencyList.some((el: any) => el.application === ApplicationEnum.AgencyCore);
        this.patientListPagination = this.$refs.patientListPagination;

        const permissionChecker = (Id: number, ParentPermission: any, Action: any, showReferral: boolean) => {
            return this._permissionManager.checkPermissionByProduct(ParentPermission, Action, Id) && showReferral;
        };

        this.checkProductPermission = {
            showNewReferralHospice: permissionChecker(
                this.hospiceApplicationId,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralHos
            ),
            showNewReferralPalliative: permissionChecker(
                this.palliativeApplicationId,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralPal
            ),
            showNewReferralHomeHealth: permissionChecker(
                this.homeHealthApplicationId,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralHh
            ),
            showNewReferralHomeCare: permissionChecker(
                this.homeCareApplicationId,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralHc
            )
        };

        this.showNewReferral =
            this.checkProductPermission.showNewReferralHospice ||
            this.checkProductPermission.showNewReferralPalliative ||
            this.checkProductPermission.showNewReferralHomeHealth ||
            this.checkProductPermission.showNewReferralHomeCare;

        this.hasDocViewPermission = {
            Hospice: permissionChecker(
                this.hospiceApplicationId,
                ParentPermissionEnum.chart,
                PermissionActionEnum.canExport,
                showNewReferralHos
            ),
            Palliative: permissionChecker(
                this.palliativeApplicationId,
                ParentPermissionEnum.chart,
                PermissionActionEnum.canExport,
                showNewReferralPal
            ),
            HomeHealth: permissionChecker(
                this.homeHealthApplicationId,
                ParentPermissionEnum.chart,
                PermissionActionEnum.canExport,
                showNewReferralHh
            ),
            HomeCare: permissionChecker(
                this.homeCareApplicationId,
                ParentPermissionEnum.chart,
                PermissionActionEnum.canExport,
                showNewReferralHc
            )
        };
        if (!!this.$route.query?.inquiryId && !!this.$route.query?.application && !!this.$route.query?.accountId) {
            this.currentApplication = Number(this.$route.query.application);
            const inquiryByIdParams = {
                application: this.$route.query.application,
                agencyId: this.$route.query.accountId
            };
            this.inquiry = await this._centralIntakeService.getInquiryById(
                this.$route.query.inquiryId,
                inquiryByIdParams
            );
        }
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        isApplicationHospice(): boolean {
            return this.lineOfService.application == ApplicationEnum.AxxessHospiceFE;
        },

        hasData() {
            return this.page?.itemCount > 0;
        }
    },

    watch: {
        searchPatientTerm(newValue) {
            this.searchPatientTerm = newValue.trim();
        },

        selectedBranch(newValue: any, oldValue) {
            if (!this.lineOfService) {
                return;
            }
            if (!newValue) {
                this.lineOfService.lineOfService = null;
                this.lineOfService.locationId = "";
                return;
            }
            if (!this.providerList?.length) {
                this.primaryLineOfServiceList = [];
                return;
            }
            this.lineOfService.locationId = this.selectedBranch.id;
            this.accountId = this.selectedBranch.accountId;
        },

        inquiry: {
            handler() {
                if (!!this.inquiry) {
                    this.lineOfService.firstName = this.inquiry.firstName;
                    this.lineOfService.lastName = this.inquiry.lastName;
                    if (this.inquiry.firstName || this.inquiry.lastName) {
                        this.searchPatientTerm = formatName(this.inquiry.firstName, this.inquiry.lastName);
                        this.search();
                    }
                }
            },
            deep: true
        },

        sortBy() {
            if (this.patientListPagination && this.hasData) {
                this.patientListPagination.reset();
            }
        },

        selectedBranches: {
            handler() {
                if (this.searchPatientTerm.length) {
                    this.resetList();
                }
            },
            deep: true
        }
    },

    methods: {
        updateBranches(branches: string[]) {
            this.selectedBranches.length = 0;
            this.selectedBranches.push(...branches);
        },

        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.resetList();
            }
        },

        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.resetList();
            }
        },

        initNewPatientReferral(applcationId: ApplicationEnum, data?: LineOfService) {
            return new LineOfService(applcationId, data);
        },

        async pageChangedCallback(pageNumber: number = 1, pageSize: number = 10) {
            if (this.selectedBranches.length === 0) {
                return PAGE_FAIL_RESPONSE;
            }
            try {
                this.isError = false;
                this.showPatientList = true;
                this.isSearchLoading = true;
                let sortBy = this.sortBy == "serviceLine" ? "application" : this.sortBy;
                const payload: IGetPatientsByNameQuery = {
                    page: pageNumber,
                    pageSize: pageSize,
                    sortBy: sortBy,
                    order: this.sortOrder,
                    term: this.searchPatientTerm,
                    locationIds: this.selectedBranches
                };
                const data: IGetPatientsByNameResponse = await this._patientsService.getPatientsByName(payload);
                this.page = data;
                return {
                    totalPageCount: data.pageCount,
                    success: data.itemCount > 0,
                    totalCount: data.itemCount
                };
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isSearchLoading = false;
            }
        },

        isFieldInvalid(field: string) {
            let properties = this.v$.$errors.map((error: any) => error.$property);
            if (field === "branch") {
                return properties.includes("id") ? true : false;
            } else {
                return properties.includes("selectedPrimaryLineOfService") ? true : false;
            }
        },

        async addReferralForSelectedPatient() {
            const validated = await this.v$.$validate();
            if (validated) {
                try {
                    this.isAddingReferralLoading = true;
                    let params = {
                        agencyId: this.accountId,
                        accountId: this.accountId,
                        application: this.lineOfService.application
                    };
                    this.lineOfService.lineOfService = +this.selectedPrimaryLineOfService;
                    if (
                        this.lineOfService.application == this.hospiceApplicationId ||
                        this.lineOfService.application == this.palliativeApplicationId
                    ) {
                        const payload: ICreateInterimReferralPayload = { ...this.lineOfService, ...params };
                        let referralId;
                        if (!!this.inquiry) {
                            if (this.currentApplication == ApplicationEnum.AxxessPalliative) {
                                payload.lineOfService = LineOfServices.enum.PalliativeCare;
                            }
                            const response = await this._centralIntakeService.convertToReferral(
                                this.inquiry.id,
                                payload
                            );
                            referralId = response.id;
                        } else {
                            const response = await this._referralService.createInterimReferral(payload);
                            referralId = response.referralId;
                        }
                        if (this.hasInquiryId) {
                            this.$router.push(
                                `/axxess-central/create/referral?id=${referralId}&accountId=${this.accountId}&application=${this.lineOfService.application}&inquiryId=${this.$route.query.inquiryId}&isCreate=true`
                            );
                        } else {
                            this.$router.push(
                                `/axxess-central/create/referral?id=${referralId}&accountId=${this.accountId}&application=${this.lineOfService.application}&isCreate=true&isConvertBackToPending=${this.isConvertBackToPending}`
                            );
                        }
                    } else if (this.lineOfService.application == this.homeHealthApplicationId) {
                        this.$router.push(
                            `/axxess-central/create/homehealth-referral?id=0000000-0000-0000-0000-0000000000000&accountId=${
                                this.accountId
                            }&application=${this.lineOfService.application}&isCreate=true&fName=${
                                this.lineOfService.firstName
                            }&lName=${this.lineOfService.lastName}&locationId=${
                                this.lineOfService.locationId
                            }&convertToReferral=true&inquiryId=${this.inquiry ? this.inquiry.id : ""}`
                        );
                    } else if (this.lineOfService.application == this.homeCareApplicationId) {
                        this.$router.push({
                            path: "/axxess-central/create/homecare-referral",
                            query: {
                                accountId: this.accountId,
                                isNonMedical: String(this.isNonMedical),
                                isCreate: "true",
                                fName: this.lineOfService.firstName,
                                lName: this.lineOfService.lastName,
                                locationId: this.lineOfService.locationId,
                                inquiryId: this.inquiry ? this.inquiry.id : ""
                            }
                        });
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    this.isAddingReferralLoading = false;
                    this._analyticsService.logEvent({
                        category: AnalyticsCategory.CentralIntake,
                        action: "Central New referral"
                    });
                }
            } else {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fix the validation errors in the form.`
                });
            }
        },

        openLineOfService(applicationId: ApplicationEnum, isNonMedical?: boolean) {
            this.v$.$reset();
            if (typeof isNonMedical !== "undefined") {
                this.isNonMedical = isNonMedical;
            }

            this.isLineOfServiceFormRequested = true;
            if (!this.inquiry) {
                this.lineOfService = this.initNewPatientReferral(applicationId);
            }

            this.lineOfService.application = applicationId;
            this.providerList = [];

            let inquiryProviderId = null;
            if (this.inquiry) {
                inquiryProviderId = this.allBranchList
                    ?.filter((branch) => branch.id === this.inquiry.locationId)
                    ?.map((branch) => branch.providerId)
                    ?.pop();
            }

            if ([ApplicationEnum.AxxessHospiceFE, ApplicationEnum.AxxessPalliative].includes(applicationId)) {
                this.providerList = this.allProviderList.filter(
                    (provider) =>
                        provider.branches[0].application == applicationId &&
                        (!inquiryProviderId || provider.id === inquiryProviderId)
                );

                this.providerList.map((provider) => {
                    if (provider.branches[0].application == applicationId) {
                        this.primaryLineOfServiceList = provider.lineOfService;
                        this.selectedPrimaryLineOfService = provider.lineOfService[0];
                    }
                });

                const result = this.providerList.find((provider) =>
                    provider.branches.find(
                        (branch: any) =>
                            branch.accountId === this.$route.query.locationId && branch.application === applicationId
                    )
                );

                if (result) {
                    this.selectedBranch = result.branches.find(
                        (branch: any) =>
                            branch.accountId === this.$route.query.locationId && branch.application === applicationId
                    );
                }

                if (this.$route.query.firstName) {
                    let data = {
                        locationId: this.$route.query.locationId as string,
                        firstName: this.firstName,
                        lastName: this.lastName,
                        lineOfService: result.lineOfService[0],
                        application: applicationId
                    };

                    this.lineOfService = this.initNewPatientReferral(applicationId, data);
                }
            } else {
                let providers: { [key: string]: string } = {};
                this.allBranchList.forEach((item) => {
                    if (item.providerId && item.application == applicationId) {
                        providers[item.providerId] = item.providerName;
                    }
                });
                for (let providerItem of Object.keys(providers)) {
                    let obj: IGetProviderItem = { id: null, name: null, branches: null };
                    let branch: IBranchResponse[] = [];
                    this.allBranchList.forEach((item) => {
                        if (providerItem == item.providerId && item.application == applicationId) {
                            branch.push(item);
                        }
                    });
                    obj.id = providerItem;
                    obj.branches = branch;
                    obj.name = providers[providerItem];
                    if (!inquiryProviderId || obj.id === inquiryProviderId) {
                        this.providerList.push(obj);
                    }
                }
            }
            if (!!this.inquiry) {
                const result = this.providerList.find((provider) =>
                    provider.branches.find((branch: any) => branch.id === this.inquiry.locationId)
                );
                if (result) {
                    this.selectedBranch = result.branches.find((branch: any) => branch.id === this.inquiry.locationId);
                }
            }
        },

        cancel() {
            this.$router.back();
        },

        closeLineOfService() {
            this.isLineOfServiceFormRequested = false;
            this.lineOfService = this.initNewPatientReferral(this.lineOfService.application);
            this.selectedBranch = null;
            this.selectedPrimaryLineOfService = null;
        },

        search() {
            this.showPatientList = true;

            this.resetList();
        },

        resetList() {
            this.isError = false;
            this.patientListPagination = this.$refs.patientListPagination;
            this.patientListPagination?.reset();
        },

        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },

        handleConvertToPending(details: any) {
            this.isConvertBackToPending = true;
            this.isLineOfServiceFormRequested = true;
            this.providerList = this.allProviderList.filter(
                (provider) => provider.branches[0].application == details.application
            );
            const result = this.providerList.find((provider) =>
                provider.branches.find((branch: any) => branch.accountId === details.accountId)
            );

            if (result) {
                this.selectedBranch = result.branches.find((branch: any) => branch.accountId === details.accountId);
                this.selectedPrimaryLineOfService = result.lineOfService[0];
            }
            let data = {
                locationId: details.accountId as string,
                firstName: details.patientName.firstName,
                lastName: details.patientName.lastName,
                lineOfService: result.lineOfService[0],
                application: details.application
            };

            this.lineOfService = this.initNewPatientReferral(details.application, data);
        }
    }
});
